import {Checkbox, FormControl} from 'platform/components';
import {HStack, Scroll, Show} from 'platform/foundation';

import {useCallback} from 'react';
import {useFieldArray, UseFormReturn, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';

import {isNotNilOrEmpty, TestIdProps, suffixTestId, Nullish} from 'shared/web';

import {useTireSetOptions} from '../hooks/useTireSetOptions';
import {ExistingWheel} from '../types/ExistingWheel';
import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';
import {WheelCard} from './WheelCard';

type WheelKey = keyof TireSetDetailFormDefinition['wheels']['0'];

interface WheelArrayProps extends TestIdProps {
  control: FormControl<TireSetDetailFormDefinition>;
  formApi: UseFormReturn<TireSetDetailFormDefinition>;
  existingWheels?: ExistingWheel[];
  homeBranchId: string | Nullish;
}

export function WheelArray(props: WheelArrayProps) {
  const tireSetOptions = useTireSetOptions(props.homeBranchId);

  const arrayFields = useFieldArray({
    control: props.control,
    name: 'wheels',
    keyName: 'fieldId',
  });

  const checkedValues =
    useWatch({
      control: props.control,
      name: arrayFields.fields.map((_, index) => `wheels.${index}.checked` as const) ?? [],
    }) ?? [];

  const checkedWheelsCount = checkedValues.filter(Boolean).length;
  const areAllWheelsChecked =
    checkedWheelsCount === arrayFields.fields.length && arrayFields.fields.length > 0;
  const areSomeWheelsChecked = checkedWheelsCount > 0 && !areAllWheelsChecked;

  const handlePropagateValue = useCallback(
    (key: WheelKey, index: number) => {
      const wheels = props.formApi.getValues('wheels');
      const value = wheels[0][key];

      if (index !== 0 || wheels.length === 1) {
        return;
      }

      wheels.forEach((_, wheelIndex) => {
        if (wheelIndex === 0) {
          return;
        }

        props.formApi.setValue(`wheels.${wheelIndex}.${key}`, value, {
          shouldTouch: false,
          shouldValidate: false,
        });
      });
    },
    [props.formApi]
  );

  const handleSelectAllWheels = (isChecked: boolean) => {
    arrayFields.fields.forEach((_, wheelIndex) => {
      props.formApi.setValue(`wheels.${wheelIndex}.checked`, isChecked, {
        shouldTouch: false,
        shouldValidate: false,
        shouldDirty: true,
      });
    });
  };

  return (
    <>
      <Show when={isNotNilOrEmpty(props.existingWheels)}>
        <HStack spacing={4}>
          <Checkbox
            value={areAllWheelsChecked}
            isIndeterminate={areSomeWheelsChecked}
            onChange={handleSelectAllWheels}
            label={i18n.t('general.actions.selectAll')}
            data-testid={suffixTestId('selectAll', props)}
          />
        </HStack>
      </Show>
      <Scroll auto maxWidth="100%">
        <HStack spacing={4}>
          {arrayFields.fields.map((field, index) => {
            const existingWheel = props.existingWheels?.find((wheel) => wheel?.id === field.id);
            const locationOptions = tireSetOptions.getLocationOptionsWithSelectedValue(
              existingWheel?.wheelDetails?.location
            );
            const manufacturerOptions = tireSetOptions.getManufacturerOptionsWithSelectedValue(
              existingWheel?.wheelDetails?.manufacturer
            );

            return (
              <WheelCard
                key={field.fieldId}
                index={index}
                wheelId={field.id}
                control={props.control}
                existingWheels={props.existingWheels}
                locationOptions={locationOptions}
                manufacturerOptions={manufacturerOptions}
                tireSetOptions={tireSetOptions}
                onPropagateValue={handlePropagateValue}
                data-testid={props['data-testid']}
              />
            );
          })}
        </HStack>
      </Scroll>
    </>
  );
}
